import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../utils/apiUrl';

const TermsOfService = () => {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [footerCopyright, setFooterCopyright] = useState('Copyright © 2026 Blue API, All rights reserved.');

  const t = (zh, en) => (lang === 'zh' ? zh : en);

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem('lang') || 'en');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 获取服务条款内容
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(getApiUrl('/api/homepage-config'));
        const data = await response.json();
        if (data.code === 0 && data.data) {
          setContent(data.data.terms_of_service_content || '');
          if (data.data.footer_copyright) {
            setFooterCopyright(data.data.footer_copyright);
          }
        }
      } catch (error) {
        console.log('获取服务条款失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // 默认内容
  const defaultContent = lang === 'zh' ? `
    <h1>服务条款</h1>
    <p>最后更新日期：2026年1月1日</p>
    <h2>1. 接受条款</h2>
    <p>欢迎使用 Blue API 服务。通过访问或使用我们的服务，您同意受这些服务条款的约束。如果您不同意这些条款，请不要使用我们的服务。</p>
    <h2>2. 服务描述</h2>
    <p>Blue API 提供 AI 模型 API 聚合服务，允许用户通过统一的接口访问多个 AI 模型。我们保留随时修改、暂停或终止服务的权利。</p>
    <h2>3. 账户注册</h2>
    <p>要使用我们的服务，您需要注册一个账户。您同意：</p>
    <ul>
      <li>提供准确、完整的注册信息</li>
      <li>保护您的账户密码安全</li>
      <li>对账户下的所有活动负责</li>
      <li>及时通知我们任何未经授权的使用</li>
    </ul>
    <h2>4. 使用规则</h2>
    <p>您同意不会：</p>
    <ul>
      <li>使用服务进行任何非法活动</li>
      <li>干扰或破坏服务的正常运行</li>
      <li>尝试未经授权访问我们的系统</li>
      <li>滥用 API 配额或进行恶意调用</li>
      <li>转售或再分发我们的服务</li>
    </ul>
    <h2>5. 付费和退款</h2>
    <p>我们的服务采用预付费模式。您同意：</p>
    <ul>
      <li>按照公布的价格支付费用</li>
      <li>充值后余额不予退还</li>
      <li>我们有权调整价格，但会提前通知</li>
    </ul>
    <h2>6. 知识产权</h2>
    <p>我们的服务及相关内容的所有知识产权归我们所有。您仅获得有限的使用许可，不得转让或再许可。</p>
    <h2>7. 免责声明</h2>
    <p>我们的服务按"现状"提供，不作任何明示或暗示的保证。我们不对因使用服务而导致的任何损失承担责任。</p>
    <h2>8. 条款修改</h2>
    <p>我们保留随时修改这些条款的权利。修改后的条款将在网站上公布，继续使用服务即表示接受新条款。</p>
    <h2>9. 联系我们</h2>
    <p>如有任何问题，请联系我们：</p>
    <p>邮箱：support@example.com</p>
  ` : `
    <h1>Terms of Service</h1>
    <p>Last updated: January 1, 2026</p>
    <h2>1. Acceptance of Terms</h2>
    <p>Welcome to Blue API services. By accessing or using our services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.</p>
    <h2>2. Service Description</h2>
    <p>Blue API provides AI model API aggregation services, allowing users to access multiple AI models through a unified interface. We reserve the right to modify, suspend, or terminate the service at any time.</p>
    <h2>3. Account Registration</h2>
    <p>To use our services, you need to register an account. You agree to:</p>
    <ul>
      <li>Provide accurate and complete registration information</li>
      <li>Protect your account password securely</li>
      <li>Be responsible for all activities under your account</li>
      <li>Promptly notify us of any unauthorized use</li>
    </ul>
    <h2>4. Usage Rules</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Use the service for any illegal activities</li>
      <li>Interfere with or disrupt the normal operation of the service</li>
      <li>Attempt to access our systems without authorization</li>
      <li>Abuse API quotas or make malicious calls</li>
      <li>Resell or redistribute our services</li>
    </ul>
    <h2>5. Payment and Refunds</h2>
    <p>Our services use a prepaid model. You agree to:</p>
    <ul>
      <li>Pay fees according to published prices</li>
      <li>Balances after recharge are non-refundable</li>
      <li>We may adjust prices but will notify in advance</li>
    </ul>
    <h2>6. Intellectual Property</h2>
    <p>All intellectual property rights to our services and related content belong to us. You only obtain a limited license to use and may not transfer or sublicense.</p>
    <h2>7. Disclaimer</h2>
    <p>Our services are provided "as is" without any express or implied warranties. We are not liable for any losses resulting from the use of our services.</p>
    <h2>8. Changes to Terms</h2>
    <p>We reserve the right to modify these terms at any time. Modified terms will be posted on the website, and continued use of the service constitutes acceptance of the new terms.</p>
    <h2>9. Contact Us</h2>
    <p>For any questions, please contact us:</p>
    <p>Email: support@example.com</p>
  `;

  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <div className="legal-nav-container">
          <Link to="/" className="legal-logo">
            <span>Blue API</span>
          </Link>
          <div className="legal-nav-links">
            <Link to="/">{t('返回首页', 'Back to Home')}</Link>
          </div>
        </div>
      </nav>

      <main className="legal-content">
        <div className="legal-container">
          {loading ? (
            <div className="legal-loading">{t('加载中...', 'Loading...')}</div>
          ) : content ? (
            <div className="legal-html" dangerouslySetInnerHTML={{ __html: content }} />
          ) : (
            <div className="legal-html" dangerouslySetInnerHTML={{ __html: defaultContent }} />
          )}
        </div>
      </main>

      <footer className="legal-footer">
        <p>{footerCopyright}</p>
      </footer>

      <style>{`
        .legal-page {
          min-height: 100vh;
          background: #f8fafc;
        }
        .legal-nav {
          background: #fff;
          border-bottom: 1px solid #e5e7eb;
          padding: 16px 0;
        }
        .legal-nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .legal-logo {
          font-size: 20px;
          font-weight: 600;
          color: #1677ff;
          text-decoration: none;
        }
        .legal-nav-links a {
          color: #6b7280;
          text-decoration: none;
          font-size: 14px;
        }
        .legal-nav-links a:hover {
          color: #1677ff;
        }
        .legal-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 48px 24px;
        }
        .legal-container {
          background: #fff;
          border-radius: 12px;
          padding: 48px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .legal-html h1 {
          font-size: 28px;
          font-weight: 600;
          color: #111827;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 2px solid #e5e7eb;
        }
        .legal-html h2 {
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
          margin-top: 32px;
          margin-bottom: 16px;
        }
        .legal-html p {
          font-size: 15px;
          line-height: 1.8;
          color: #4b5563;
          margin-bottom: 16px;
        }
        .legal-html ul {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .legal-html li {
          font-size: 15px;
          line-height: 1.8;
          color: #4b5563;
          margin-bottom: 8px;
        }
        .legal-loading {
          text-align: center;
          padding: 48px;
          color: #6b7280;
        }
        .legal-footer {
          text-align: center;
          padding: 24px;
          color: #9ca3af;
          font-size: 14px;
          border-top: 1px solid #e5e7eb;
        }
        @media (max-width: 640px) {
          .legal-container {
            padding: 24px;
          }
          .legal-html h1 {
            font-size: 24px;
          }
          .legal-html h2 {
            font-size: 18px;
          }
        }
      `}</style>
    </div>
  );
};

export default TermsOfService;
